import { z } from 'zod';

import {
  ROUNDING_MODE,
  ROUNDING_MODE_OPTIONS,
} from '#/utils/roundingMode';

const lineSchema = z.object({
  mtrId: z.string().min(1, '請選擇物料'),
  mtrNo: z.string().optional().nullable(),
  mtrName: z.string().optional().nullable(),
  unitId: z.string().optional().nullable(),
  unitOptions: z.any().optional().nullable(),
  orderQty: z.union([z.string(), z.number(), z.null()]),
  unitPrice: z.union([z.string(), z.number(), z.null()]).optional().nullable(),
  remark: z.string().optional().nullable(),
}).superRefine((line, ctx) => {
  const rawQty = line.orderQty;
  const qty = Number(rawQty);
  if (rawQty === '' || rawQty == null || Number.isNaN(qty) || qty < 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '訂購數量須 ≥ 1',
      path: ['orderQty'],
    });
  }

  if (line.unitPrice !== '' && line.unitPrice != null) {
    const price = Number(line.unitPrice);
    if (Number.isNaN(price) || price < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '單價不可為負',
        path: ['unitPrice'],
      });
    }
  }
});

export const PURCHASE_ORDER_TAX_TYPE_OPTIONS = [
  { value: 'TAX_EXCLUDED', label: '應稅（未稅價）' },
  { value: 'TAX_INCLUDED', label: '含稅價' },
  { value: 'TAX_EXEMPT', label: '免稅' },
];

/** 與 `#/utils/roundingMode` 同源；採購／入庫／客戶訂單共用。 */
export const PURCHASE_ORDER_ROUNDING_MODE_OPTIONS = ROUNDING_MODE_OPTIONS;

export const purchaseOrderFormSchema = z.object({
  purchaseOrderDate: z.string().min(1, '請填寫採購日'),
  supplierId: z.string().min(1, '請選擇供應商'),
  currencyId: z.string().min(1, '請選擇幣別'),
  paymentTermId: z.string().min(1, '請選擇付款條件'),
  receiptWarehouseId: z.string().min(1, '請選擇預設倉'),
  /** 後端擁有；僅 UI 展示，不送 payload。 */
  exchangeRate: z.union([z.string(), z.number()]).optional().nullable(),
  taxType: z.enum(['TAX_EXCLUDED', 'TAX_INCLUDED', 'TAX_EXEMPT'], {
    message: '請選擇稅別',
  }),
  /** 後端擁有；僅 UI 預覽用，不送 payload。 */
  taxRate: z.union([z.string(), z.number()]).optional().nullable(),
  roundingMode: z.preprocess(
    (value) => (value === '' || value == null ? ROUNDING_MODE.HALF_UP : value),
    z.enum([ROUNDING_MODE.UP, ROUNDING_MODE.DOWN, ROUNDING_MODE.HALF_UP], {
      message: '請選擇進位法',
    }),
  ),
  issueReason: z.string().min(1, '請填寫採購事由'),
  remark: z.string().optional().nullable(),
  lines: z.array(lineSchema).min(1, '至少一筆明細').superRefine((lines, ctx) => {
    const seen = new Set();
    lines.forEach((line, index) => {
      const id = line.mtrId?.trim();
      if (!id) {
        return;
      }
      if (seen.has(id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: '同一物料不可重複',
          path: [index, 'mtrId'],
        });
        return;
      }
      seen.add(id);
    });
  }),
});

export const PURCHASE_ORDER_FIELD_LABELS = {
  purchaseOrderDate: '採購日',
  supplierId: '供應商',
  currencyId: '幣別',
  paymentTermId: '付款條件',
  receiptWarehouseId: '預設倉',
  exchangeRate: '匯率',
  taxType: '稅別',
  taxRate: '稅率',
  roundingMode: '進位法',
  issueReason: '採購事由',
  remark: '備註',
  lines: '明細',
  'lines.mtrId': '物料',
  'lines.unitId': '單位',
  'lines.orderQty': '訂購數量',
  'lines.unitPrice': '單價',
  'lines.remark': '行備註',
};

export const PURCHASE_ORDER_LINES_TIP =
  '多選物料後確認；同單不可重複同一物料。單價可留空由後端換算。請填訂購量與單位。';
